import { useState, useCallback, useRef } from "react";
import { API_URL } from "../../config";
import { Link } from "react-router-dom";
import CourseRoadmap from "./CourseRoadmap";
import ModuleDetail from "./ModuleDetail";
import CertificateView from "./CertificateView";
import { MindmapFlow, MindmapData } from "../mindmap/MindmapPage";
import "./course.scss";

interface Module {
  id: string;
  title: string;
  description: string;
  learning_objectives: string[];
  content: string;
  practice_guidance: string;
  order: number;
  prerequisites: string[];
  youtube_query: string;
}

interface Course {
  id: string;
  title: string;
  description: string;
  difficulty: string;
  estimated_hours: number;
  modules: Module[];
}

interface CourseProgress {
  courseId: string;
  completedModules: string[];
  startedAt: string;
  lastAccessedAt: string;
}

interface CourseHistoryEntry {
  id: string;
  title: string;
  topic: string;
  date: string;
  moduleCount: number;
}

type PageState = "select" | "config" | "loading" | "roadmap" | "certificate";

const TOPICS = [
  { name: "Data Structures & Algorithms", icon: "\u{1F9E9}" },
  { name: "Web Development", icon: "\u{1F310}" },
  { name: "Machine Learning", icon: "\u{1F916}" },
  { name: "System Design", icon: "\u{1F3D7}" },
  { name: "Database Management", icon: "\u{1F5C4}" },
  { name: "Operating Systems", icon: "\u{1F5A5}" },
  { name: "Computer Networks", icon: "\u{1F4E1}" },
  { name: "Python Programming", icon: "\u{1F40D}" },
];

function loadProgress(courseId: string): CourseProgress {
  const key = `course_progress_${courseId}`;
  const saved = localStorage.getItem(key);
  if (saved) {
    try { return JSON.parse(saved); } catch {}
  }
  return {
    courseId,
    completedModules: [],
    startedAt: new Date().toISOString(),
    lastAccessedAt: new Date().toISOString(),
  };
}

function saveProgress(progress: CourseProgress) {
  const key = `course_progress_${progress.courseId}`;
  progress.lastAccessedAt = new Date().toISOString();
  localStorage.setItem(key, JSON.stringify(progress));
}

function loadHistory(): CourseHistoryEntry[] {
  try {
    return JSON.parse(localStorage.getItem("course_history") || "[]");
  } catch { return []; }
}

function saveToHistory(course: Course, topic: string) {
  const history = loadHistory();
  const entry: CourseHistoryEntry = {
    id: course.id,
    title: course.title,
    topic,
    date: new Date().toISOString().split("T")[0],
    moduleCount: course.modules.length,
  };
  const filtered = history.filter((h) => h.id !== course.id);
  filtered.unshift(entry);
  localStorage.setItem("course_history", JSON.stringify(filtered.slice(0, 5)));
  localStorage.setItem(`course_data_${course.id}`, JSON.stringify(course));
}

function loadCachedCourse(courseId: string): Course | null {
  try {
    const data = localStorage.getItem(`course_data_${courseId}`);
    return data ? JSON.parse(data) : null;
  } catch { return null; }
}

export default function CoursePage() {
  const [pageState, setPageState] = useState<PageState>("select");
  const [course, setCourse] = useState<Course | null>(null);
  const [progress, setProgress] = useState<CourseProgress>({
    courseId: "", completedModules: [], startedAt: "", lastAccessedAt: "",
  });
  const [selectedModuleId, setSelectedModuleId] = useState<string | null>(null);
  const [customTopic, setCustomTopic] = useState("");
  const [selectedTopic, setSelectedTopic] = useState("");
  const [flashcardCount, setFlashcardCount] = useState(5);
  const [quizCount, setQuizCount] = useState(5);
  const [error, setError] = useState("");
  const [detailWidth, setDetailWidth] = useState(420);
  const isResizing = useRef(false);
  const [mindmapData, setMindmapData] = useState<MindmapData | null>(null);
  const [mindmapLoading, setMindmapLoading] = useState(false);
  const [roadmapView, setRoadmapView] = useState<"roadmap" | "mindmap">("roadmap");

  const handleMouseDown = useCallback(() => {
    isResizing.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing.current) return;
      const newWidth = window.innerWidth - e.clientX;
      const clamped = Math.max(320, Math.min(newWidth, window.innerWidth * 0.6));
      setDetailWidth(clamped);
    };

    const handleMouseUp = () => {
      isResizing.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  }, []);

  const handleTopicSelect = (topic: string) => {
    setSelectedTopic(topic);
    setPageState("config");
  };

  const loadFromHistory = (entry: CourseHistoryEntry) => {
    const cached = loadCachedCourse(entry.id);
    if (cached) {
      setCourse(cached);
      setSelectedTopic(entry.topic);
      setProgress(loadProgress(cached.id));
      setRoadmapView("roadmap");
      setMindmapData(null);
      try {
        const mm = localStorage.getItem(`mindmap_data_${entry.topic}`);
        if (mm) setMindmapData(JSON.parse(mm));
      } catch {}
      setPageState("roadmap");
    }
  };

  const generateCourse = useCallback(async () => {
    setPageState("loading");
    setError("");
    try {
      const resp = await fetch(`${API_URL}/api/course/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ topic: selectedTopic }),
      });
      const data = await resp.json();
      if (data.status === "success" && data.course?.modules) {
        const c = data.course as Course;
        setCourse(c);
        const p = loadProgress(c.id);
        setProgress(p);
        saveToHistory(c, selectedTopic);
        setPageState("roadmap");
        setRoadmapView("roadmap");
        setMindmapData(null);
        setMindmapLoading(true);
        fetch(`${API_URL}/api/mindmap`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ topic: selectedTopic }),
        })
          .then((r) => r.json())
          .then((mm) => {
            if (mm.status === "success" && mm.mindmap) {
              let raw = mm.mindmap;
              // If backend couldn't parse JSON and returned raw_output, try client-side
              if (raw.raw_output && !raw.central_topic && !raw.branches) {
                const s: string = raw.raw_output;
                const fenceMatch = s.match(/```(?:json)?\s*([\s\S]*?)\s*```/i);
                const candidate = fenceMatch ? fenceMatch[1] : s;
                try { raw = JSON.parse(candidate); } catch {
                  const braceMatch = candidate.match(/\{[\s\S]*\}/);
                  if (braceMatch) try { raw = JSON.parse(braceMatch[0]); } catch {}
                }
              }
              const normalizeBranch = (b: any): any => {
                if (!b) return { name: "" };
                if (typeof b === "string") return { name: b };
                const subs = b.sub_branches || b.subbranches || b.subBranches || [];
                return { name: b.title || b.name || "", subbranches: Array.isArray(subs) ? subs.map(normalizeBranch) : [] };
              };
              const topic = raw.central_topic || raw.topic || raw.title || "";
              const branches = raw.branches || raw.children || [];
              if (topic) {
                const mmData = { topic, branches: branches.map(normalizeBranch) };
                setMindmapData(mmData);
                try { localStorage.setItem(`mindmap_data_${selectedTopic}`, JSON.stringify(mmData)); } catch {}
              }
            }
          })
          .catch(() => {})
          .finally(() => setMindmapLoading(false));
      } else {
        setError(data.message || "Failed to generate course");
        setPageState("config");
      }
    } catch (e: any) {
      setError(e.message || "Network error");
      setPageState("config");
    }
  }, [selectedTopic]);

  const handleModuleClick = useCallback((moduleId: string) => {
    setSelectedModuleId(moduleId);
  }, []);

  const handleMarkComplete = useCallback((moduleId: string) => {
    setProgress((prev) => {
      const updated = { ...prev, completedModules: [...prev.completedModules, moduleId] };
      saveProgress(updated);
      return updated;
    });
  }, []);

  const allComplete =
    course &&
    course.modules.length > 0 &&
    course.modules.every((m) => progress.completedModules.includes(m.id));

  const selectedModule = course?.modules.find((m) => m.id === selectedModuleId);

  const isModuleLocked = (mod: Module) =>
    mod.prerequisites.length > 0 &&
    !mod.prerequisites.every((p) => progress.completedModules.includes(p));

  const progressPercent = course
    ? Math.round((progress.completedModules.length / course.modules.length) * 100)
    : 0;

  const history = loadHistory();

  return (
    <div className="course-page">
      <div className="course-header">
        <Link to="/study" className="course-back-link">&larr; Back</Link>
        <h1>
          {pageState === "select" || pageState === "config"
            ? "Course Generator"
            : course?.title || "Course"}
        </h1>
        {pageState === "roadmap" && allComplete && (
          <button
            className="course-back-link"
            style={{ background: "rgba(78,205,196,0.2)", color: "#4ECDC4", border: "1px solid #4ECDC4", cursor: "pointer" }}
            onClick={() => setPageState("certificate")}
          >
            Get Certificate
          </button>
        )}
      </div>

      {pageState === "roadmap" && course && (
        <div className="course-progress-bar">
          <div className="progress-text">
            {progress.completedModules.length} / {course.modules.length} modules complete ({progressPercent}%)
          </div>
          <div className="progress-track">
            <div className="progress-fill" style={{ width: `${progressPercent}%` }} />
          </div>
        </div>
      )}

      {pageState === "select" && (
        <div className="course-select">
          <h2>Choose a Topic</h2>
          <p>Select a topic to generate your personalized learning path</p>
          <div className="topic-grid">
            {TOPICS.map((t) => (
              <div key={t.name} className="topic-card" onClick={() => handleTopicSelect(t.name)}>
                <div className="topic-icon">{t.icon}</div>
                <div className="topic-name">{t.name}</div>
              </div>
            ))}
          </div>
          <div className="custom-topic-form">
            <input
              type="text"
              placeholder="Or enter a custom topic..."
              value={customTopic}
              onChange={(e) => setCustomTopic(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && customTopic.trim()) handleTopicSelect(customTopic.trim());
              }}
            />
            <button onClick={() => customTopic.trim() && handleTopicSelect(customTopic.trim())}>
              Next
            </button>
          </div>

          {history.length > 0 && (
            <div className="recent-courses-list">
              <h3>Recent Courses</h3>
              <div className="history-list">
                {history.map((h) => (
                  <div key={h.id} className="recent-course-item" onClick={() => loadFromHistory(h)}>
                    <div className="item-title">{h.title}</div>
                    <div className="item-meta">{h.moduleCount} modules &middot; {h.date}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {error && <div className="error-msg">{error}</div>}
        </div>
      )}

      {pageState === "config" && (
        <div className="course-config">
          <h2>Configure Your Course</h2>
          <div className="config-topic">
            Topic: <strong>{selectedTopic}</strong>
          </div>

          <div className="config-slider">
            <label>
              Flashcards per module: <strong>{flashcardCount}</strong>
            </label>
            <input
              type="range" min={3} max={10} value={flashcardCount}
              onChange={(e) => setFlashcardCount(Number(e.target.value))}
            />
            <div className="slider-range"><span>3</span><span>10</span></div>
          </div>

          <div className="config-slider">
            <label>
              Quiz questions per module: <strong>{quizCount}</strong>
            </label>
            <input
              type="range" min={3} max={10} value={quizCount}
              onChange={(e) => setQuizCount(Number(e.target.value))}
            />
            <div className="slider-range"><span>3</span><span>10</span></div>
          </div>

          <div className="config-actions">
            <button className="config-back" onClick={() => setPageState("select")}>
              &larr; Back
            </button>
            <button className="config-generate" onClick={generateCourse}>
              Generate Course
            </button>
          </div>
          {error && <div className="error-msg">{error}</div>}
        </div>
      )}

      {pageState === "loading" && (
        <div className="course-loading">
          <div className="spinner" />
          <p>Generating course structure for <strong>{selectedTopic}</strong>...</p>
          <p style={{ fontSize: "0.8rem", color: "#555" }}>This should take just a few seconds</p>
        </div>
      )}

      {pageState === "roadmap" && course && (
        <div className="course-roadmap-layout">
          <div className="course-roadmap-container">
            <div className="roadmap-view-toggle">
              <button
                className={roadmapView === "roadmap" ? "active" : ""}
                onClick={() => setRoadmapView("roadmap")}
              >
                Roadmap
              </button>
              <button
                className={roadmapView === "mindmap" ? "active" : ""}
                onClick={() => setRoadmapView("mindmap")}
                disabled={mindmapLoading || !mindmapData}
              >
                {mindmapLoading ? "Generating mindmap..." : "Mindmap"}
              </button>
            </div>
            {roadmapView === "roadmap" ? (
              <CourseRoadmap
                modules={course.modules}
                progress={progress}
                selectedModuleId={selectedModuleId}
                onModuleClick={handleModuleClick}
              />
            ) : (
              mindmapData && <MindmapFlow data={mindmapData} />
            )}
          </div>
          {selectedModule && (
            <>
              <div className="resize-handle" onMouseDown={handleMouseDown}>
                <div className="resize-handle-line" />
              </div>
              <div style={{ width: detailWidth, minWidth: 320, maxWidth: "60vw", height: "100%", overflow: "hidden" }}>
                <ModuleDetail
                  module={selectedModule}
                  courseTopic={selectedTopic}
                  isCompleted={progress.completedModules.includes(selectedModule.id)}
                  isLocked={isModuleLocked(selectedModule)}
                  numFlashcards={flashcardCount}
                  numQuiz={quizCount}
                  onMarkComplete={handleMarkComplete}
                  onClose={() => setSelectedModuleId(null)}
                />
              </div>
            </>
          )}
        </div>
      )}

      {pageState === "certificate" && course && (
        <CertificateView course={course} onBack={() => setPageState("roadmap")} />
      )}
    </div>
  );
}
